import { getConfig } from "@/lib/config";
import { csrfToken } from "@/auth/csrf";
import { CORRELATION_HEADER, newCorrelationId } from "@/lib/correlation";
import type { SerializedEvent } from "./contract";

// Diagnostic delivery transport (ticket #127). A deliberately LOW-LEVEL
// same-origin POST — NOT routed through the Apollo/HTTP error seams or the
// shared api-request reporting path — so a delivery failure can never become a
// new diagnostic (no recursion). Each delivery request mints its OWN
// request-scoped correlation ID, distinct from any event's operation
// correlation ID.

export type DeliveryOutcome =
  | { kind: "ack" }
  | { kind: "retry" }
  | { kind: "retry-after"; retryAfterMs: number }
  | { kind: "drop" }
  | { kind: "clear-auth" }
  | { kind: "csrf-refresh" };

export const RETRY_AFTER_CAP_MS = 60_000;

// Pure status → outcome classification (0127 table). Exposed for unit tests.
export function classifyStatus(status: number, retryAfterHeader?: string | null): DeliveryOutcome {
  if (status === 202 || status === 200) return { kind: "ack" };
  if (status === 429) {
    const seconds = Number(retryAfterHeader);
    const ms = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 1000;
    return { kind: "retry-after", retryAfterMs: Math.min(ms, RETRY_AFTER_CAP_MS) };
  }
  if (status === 408 || status === 425 || status >= 500) return { kind: "retry" };
  if (status === 401) return { kind: "clear-auth" };
  if (status === 403) return { kind: "csrf-refresh" };
  // 400/413/415 and any other 4xx: retrying can't make the batch valid.
  return { kind: "drop" };
}

export type DeliverFn = (
  events: SerializedEvent[],
  opts?: { keepalive?: boolean; signal?: AbortSignal; metrics?: Record<string, number> },
) => Promise<DeliveryOutcome>;

export const deliver: DeliverFn = async (events, opts = {}) => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    [CORRELATION_HEADER]: newCorrelationId(),
  };
  if (opts.metrics && Object.keys(opts.metrics).length > 0) {
    headers["X-Diagnostics-Metrics"] = JSON.stringify(opts.metrics);
  }
  const token = csrfToken();
  if (token) headers["X-CSRF-Token"] = token;

  let response: Response;
  try {
    response = await fetch(`${getConfig().basePath}diagnostics`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ events }),
      keepalive: opts.keepalive,
      signal: opts.signal,
    });
  } catch {
    // Network failure / abort: transient, worth a bounded retry.
    return { kind: "retry" };
  }
  return classifyStatus(response.status, response.headers.get("retry-after"));
};
